import { Skeleton, Tooltip } from 'antd'
import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { cx } from '@/Ship/Helpers/cssClass'

import styles from './Dashboard.module.css'

export interface FunnelStage {
    key: string
    label: string
    color: string
    count: number
    /** Переходи у список етапу; для задач — дві сторінки (з заявками / без) */
    links: { key: string; label: string; to: string }[]
}

interface FunnelProps {
    stages: FunnelStage[]
    loading: boolean
    ariaLabel: string
}

/**
 * Воронка етапів: одна смуга з частками етапів і таблиця рядків «позначка · назва · число ·
 * переходи». У колонках назви етапів обрізалися вже на ноутбуці; рядки читаються на
 * будь-якій ширині. Нульові етапи приглушені, а не прибрані — порядок етапів лишається
 * впізнаваним.
 */
export const Funnel = ({ stages, loading, ariaLabel }: FunnelProps) => {
    const total = stages.reduce((sum, stage) => sum + stage.count, 0)
    const share = (count: number): number => (total > 0 ? Math.round((count / total) * 100) : 0)

    return (
        <div className={cx(styles, 'funnel')}>
            <div className={cx(styles, 'funnelBar')} aria-hidden="true">
                {!loading &&
                    stages
                        .filter((stage) => stage.count > 0)
                        .map((stage) => (
                            <Tooltip
                                key={stage.key}
                                title={`${stage.label}: ${stage.count} (${share(stage.count) || '<1'}%)`}
                            >
                                {/* Частка — через flex-grow: зазори між сегментами не збивають пропорцію */}
                                <i style={{ flexGrow: stage.count, background: stage.color }} />
                            </Tooltip>
                        ))}
            </div>
            <table className={cx(styles, 'stages')} aria-label={ariaLabel} aria-busy={loading}>
                <tbody>
                    {stages.map((stage) => (
                        <tr
                            key={stage.key}
                            className={`${cx(styles, 'stage')} ${stage.count === 0 ? cx(styles, 'stageZero') : ''}`}
                            style={{ '--stage-color': stage.color } as CSSProperties}
                        >
                            <th scope="row" className={cx(styles, 'stageName')}>
                                <span className={cx(styles, 'stageHead')}>
                                    <i className={cx(styles, 'stageDot')} aria-hidden="true" />
                                    <span>{stage.label}</span>
                                </span>
                            </th>
                            <td className={cx(styles, 'stageValue')}>
                                {loading ? (
                                    <Skeleton.Input
                                        active
                                        size="small"
                                        style={{ width: 32, minWidth: 32, height: 20 }}
                                    />
                                ) : (
                                    stage.count
                                )}
                            </td>
                            <td className={cx(styles, 'stageLinks')}>
                                {!loading &&
                                    stage.count > 0 &&
                                    stage.links.map((link) => (
                                        <Link
                                            key={link.key}
                                            className={cx(styles, 'miniLink')}
                                            to={link.to}
                                            aria-label={`${stage.label}: ${link.label}`}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
